import "./App.css";
import { Route, Routes, Navigate } from "react-router-dom";
import Students from "./Components/Pages/Students/Students.tsx";
import Results from "./Components/Pages/Result/Results.tsx";
import Register from "./Components/Pages/Authentication/Register.tsx";
import ForgotPassword from "./Components/Pages/Authentication/ForgotPassword.tsx";
import NoAuthLayout from "./Components/Pages/Layouts/NoAuthLayout.tsx";
import ResetPassword from "./Components/Pages/Authentication/ResetPassword.tsx";



import SchoolSettings from "./Components/Pages/Settings/SchoolSettings/SchoolSettings.tsx";
import SessionSettings from "./Components/Pages/Settings/SessionSettings/SessionSettings.tsx";
import TermSettings from "./Components/Pages/Settings/TermSettings/TermSettings.tsx";
import Login from "./Components/Pages/Authentication/Login.tsx";
import GradeCriteriaSettings from "./Components/Pages/Settings/GradeCriteriaSettings/GradeCriteriaSettings.tsx";
import LevelSettings from "./Components/Pages/Settings/LevelSettings/LevelSettings.tsx";
import ClassSettings from "./Components/Pages/Settings/ClassSettings/ClassSettings.tsx";
import UserSettings from "./Components/Pages/Settings/UserSettings/UserSettings.tsx";
import SubjectSettings from "./Components/Pages/Settings/SubjectSettings/SubjectSettings.tsx";
import AssessmentTypeSettings from "./Components/Pages/Settings/AssessmentTypeSettings/AssessmentTypeSettings.tsx";
import AuditTrailSettings from "./Components/Pages/Settings/AuditTrailSettings/AuditTrailSettings.tsx";
import DashboardGrid from "./Components/Pages/Dashboard/DashboardGrid.tsx";
import Help from "./Components/Pages/Help.tsx";
import { useCombinedLoadingContext } from "./Contexts/CombinedLoadingContext";
import Loader from "./Components/CustomUi/Loader.tsx";
import { FullPageError } from "./Components/CustomUi/CustomAlert.tsx";
import BulkReport from "./Components/Pages/Result/BulkReport";
import LandingPage from "./Components/Pages/Landing/LandingPage.tsx";
import ErrorRoute from "./services/ErrorRoute.tsx";
import ExportData from "./Components/Pages/Settings/DataManagement/ExportData";
import DeleteData from "./Components/Pages/Settings/DataManagement/DeleteData";
import NotificationSettings from "./Components/Pages/Settings/NotificationSettings/NotificationSettings";
import Profile from "./Components/Pages/Profile/Profile";
import PromotionEligibilityPage from "./Components/Pages/Promotion/PromotionEligibilityPage.tsx";
import Promotion from "./Components/Pages/Promotion/Promotion.tsx";
import ActivateAccount from "./Components/Pages/Authentication/ActivateAccount.tsx";
import PrivateRoute from "./Components/Pages/Layouts/PrivateRoute.tsx";
import Layout from "./Components/Pages/Layouts/Layout.tsx";
import { AdminRoute } from "./Components/Pages/Layouts/AdminRoute.tsx";
import WalletsPage from "./Components/Pages/Finance/WalletsPage.tsx";
import FeeCategoriesPage from "./Components/Pages/Finance/FeeCategoriesPage.tsx";
import VirtualAccountsPage from "./Components/Pages/Finance/VirtualAccountsPage.tsx";

import DemoPage from "./Components/Pages/Common/DemoPage";
import AboutPage from "./Components/Pages/Common/AboutPage";
import ContactPage from "./Components/Pages/Common/ContactPage";
import FeaturesPage from "./Components/Pages/Common/FeaturesPage";
import PricingPage from "./Components/Pages/Common/PricingPage";
import AffiliatesPage from "./Components/Pages/Common/AffiliatesPage";
import BlogPage from "./Components/Pages/Common/BlogPage";
import BlogPostPage from "./Components/Pages/Common/BlogPostPage";
import NotFoundPage from "./Components/Pages/Common/NotFoundPage";
import TermsPage from "./Components/Pages/Legal/TermsPage";
import PrivacyPage from "./Components/Pages/Legal/PrivacyPage";
import ScrollToTop from "./Components/CustomUi/ScrollToTop";
import BackToTop from "./Components/CustomUi/BackToTop";

function App() {
  const { combinedLoading } = useCombinedLoadingContext();

  if (combinedLoading) {
    return <Loader />;
  }

  return (
    <>
      <ScrollToTop />
      <BackToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="error" element={<FullPageError />} />
        <Route path="help" element={<Help />} />
        <Route path="/demo" element={<DemoPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/affiliates" element={<AffiliatesPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route path="/register" element={<Navigate to="/auth/register" replace />} />
        <Route path="auth" element={<NoAuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgotpassword" element={<ForgotPassword />} />
          <Route path="resetpassword" element={<ResetPassword />} />
          <Route path="activateaccount" element={<ActivateAccount />} />
        </Route>

        {/* Protected Routes */}
        <Route element={<ErrorRoute />}>
          <Route element={<PrivateRoute />}>
            {/* Main Layout Routes */}
            <Route path="/dashboard" element={<Layout />}>
              <Route index element={<DashboardGrid />} />
              <Route path="students" element={<Students />} />
              <Route path="results" element={<Results />} />
              <Route path="profile" element={<Profile />} />

              {/* Admin Routes - Protected by AdminRoute */}
              <Route element={<AdminRoute />}>
                {/* Admin Hub Routes - Integrated into main layout */}
                <Route path="admin/school" element={<SchoolSettings />} />

                {/* Academic Management */}
                <Route path="admin/academic">
                  <Route path="sessions" element={<SessionSettings />} />
                  <Route path="terms" element={<TermSettings />} />
                  <Route path="levels" element={<LevelSettings />} />
                  <Route path="classes" element={<ClassSettings />} />
                  <Route path="subjects" element={<SubjectSettings />} />
                  <Route
                    path="assessment-types"
                    element={<AssessmentTypeSettings />}
                  />
                  <Route path="grades" element={<GradeCriteriaSettings />} />
                </Route>

                {/* User Management */}
                <Route path="admin/users" element={<UserSettings />} />
                <Route
                  path="admin/audit-trail"
                  element={<AuditTrailSettings />}
                />
                <Route
                  path="admin/notifications"
                  element={<NotificationSettings />}
                />

                {/* Data Operations */}
                <Route path="admin/data">
                  <Route path="export" element={<ExportData />} />
                  <Route path="delete" element={<DeleteData />} />
                  <Route path="bulk-reports" element={<BulkReport />} />
                </Route>

                {/* Promotion System */}
                <Route path="admin/promotion" element={<Promotion />} />
                <Route
                  path="admin/promotion/eligibility"
                  element={<PromotionEligibilityPage />}
                />

                {/* Financial Management */}
                <Route path="admin/finance">
                  <Route path="wallets" element={<WalletsPage />} />
                  <Route path="categories" element={<FeeCategoriesPage />} />
                  <Route path="virtual-accounts" element={<VirtualAccountsPage />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>

        {/* Catch-all 404 Route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

export default App;
